import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Check, CreditCard, User, FileText, DollarSign, Home, Plus } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useLanguage } from "@/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ListPropertyPage = () => {
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [agentData, setAgentData] = useState({
    email: "",
    name: "",
    phone: ""
  });
  const [selectedPlan, setSelectedPlan] = useState("basic");
  const [loading, setLoading] = useState(false);
  const [agentId, setAgentId] = useState(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [properties, setProperties] = useState([]);
  
  const [propertyForm, setPropertyForm] = useState({
    title: "",
    description: "",
    price: "",
    listing_type: "buy",
    property_type: "house",
    bedrooms: "3",
    bathrooms: "2",
    sqft: "",
    address: "",
    city: "",
    province: "QC",
    image_url: "",
    features: ""
  });

  const provinces = ["AB", "BC", "MB", "NB", "NL", "NS", "NT", "NU", "ON", "PE", "QC", "SK", "YT"];

  const plans = [
    { 
      id: "basic", 
      name: "Standard", 
      price: 12, 
      features: ["5 propriétés", "Photos standard", "Support email"],
      maxProperties: 5
    }
  ];

  // Check for payment success return
  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    const agentIdParam = searchParams.get("agent_id");
    
    if (sessionId && agentIdParam) {
      setAgentId(agentIdParam);
      pollPaymentStatus(sessionId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    if (attempts >= 10) return;

    try {
      const response = await axios.get(`${API}/checkout/status/${sessionId}`);
      if (response.data.payment_status === "paid") {
        setPaymentSuccess(true);
        setStep(4);
        toast.success("Paiement réussi! Vous pouvez maintenant ajouter vos propriétés.");
      } else {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
      }
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  const handleAgentSubmit = async (e) => {
    e.preventDefault();
    if (!agentData.email || !agentData.name || !agentData.phone) {
      toast.error("Veuillez remplir tous les champs");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/agents`, agentData);
      setAgentId(response.data.id);
      toast.success("Compte créé!");
      setStep(2);
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleStripePayment = async () => {
    setLoading(true);
    try {
      const listingResponse = await axios.post(`${API}/owner/listing`, {
        owner_name: agentData.name,
        owner_email: agentData.email,
        owner_phone: agentData.phone,
        title: `Agent Subscription - ${agentData.name}`,
        description: "Agent subscription plan",
        price: 0,
        listing_type: "agent",
        property_type: "subscription",
        bedrooms: 0,
        bathrooms: 0,
        sqft: 0,
        address: "N/A",
        city: "N/A",
        province: "QC",
        image_url: "",
        features: [],
        plan: selectedPlan
      });

      const response = await axios.post(`${API}/checkout/create`, {
        listing_id: listingResponse.data.id,
        plan: selectedPlan,
        origin_url: `${window.location.origin}/list-property?agent_id=${agentId}`
      });

      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      toast.error("Erreur lors du paiement");
    } finally {
      setLoading(false);
    }
  };

  const handleAddProperty = async (e) => {
    e.preventDefault();
    
    if (!propertyForm.title || !propertyForm.price || !propertyForm.address || !propertyForm.city) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }

    setLoading(true);
    try {
      const propertyData = {
        title: propertyForm.title,
        description: propertyForm.description,
        price: parseFloat(propertyForm.price),
        listing_type: propertyForm.listing_type,
        property_type: propertyForm.property_type,
        bedrooms: parseInt(propertyForm.bedrooms),
        bathrooms: parseInt(propertyForm.bathrooms),
        sqft: parseInt(propertyForm.sqft) || 0,
        address: propertyForm.address,
        city: propertyForm.city,
        province: propertyForm.province,
        image_url: propertyForm.image_url || "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=800",
        images: propertyForm.image_url ? [propertyForm.image_url] : [],
        features: propertyForm.features.split(",").map(f => f.trim()).filter(f => f),
        is_featured: selectedPlan === "premium",
        owner_name: agentData.name,
        owner_email: agentData.email,
        owner_phone: agentData.phone
      };

      await axios.post(`${API}/properties`, propertyData);
      
      setProperties(prev => [...prev, propertyData]);
      toast.success("Propriété ajoutée avec succès!");
      
      // Reset form
      setPropertyForm({
        title: "",
        description: "",
        price: "",
        listing_type: "buy",
        property_type: "house",
        bedrooms: "3",
        bathrooms: "2",
        sqft: "",
        address: "",
        city: "",
        province: "QC",
        image_url: "",
        features: ""
      });
    } catch (error) {
      toast.error("Erreur lors de l'ajout de la propriété");
    } finally {
      setLoading(false);
    }
  };

  const currentPlan = plans.find(p => p.id === selectedPlan);
  const canAddMore = properties.length < (currentPlan?.maxProperties || 5);

  return (
    <div className="pt-36 pb-20 min-h-screen bg-bone" data-testid="list-property-page">
      <div className="max-w-4xl mx-auto px-6">
        <div className="text-center mb-8">
          <Badge className="bg-accent text-white mb-4">{t('listProperty.badge')}</Badge>
          <h1 className="font-heading text-4xl md:text-5xl font-medium text-charcoal mb-4">
            {t('listProperty.title')}
          </h1>
          <p className="text-muted-foreground">
            {t('listProperty.subtitle')}
          </p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-2 md:gap-4 mb-8">
          {[
            { num: 1, icon: User, label: "Compte" },
            { num: 2, icon: FileText, label: "Forfait" },
            { num: 3, icon: DollarSign, label: "Paiement" },
            { num: 4, icon: Home, label: "Propriétés" }
          ].map((s, i) => (
            <div key={`step-${s.num}`} className="flex items-center">
              <div className={`flex items-center gap-2 ${step >= s.num ? 'text-primary' : 'text-muted-foreground'}`}>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step >= s.num ? 'bg-primary text-white' : 'bg-secondary'}`}>
                  <s.icon className="w-5 h-5" />
                </div>
                <span className="hidden md:inline text-sm font-medium">{s.label}</span>
              </div>
              {i < 3 && <div className={`w-6 md:w-12 h-0.5 mx-1 md:mx-2 ${step > s.num ? 'bg-primary' : 'bg-secondary'}`} />}
            </div>
          ))}
        </div>

        <div className="bg-white rounded-2xl p-8 shadow-soft">
          {/* Step 1: Account */}
          {step === 1 && (
            <div className="space-y-6">
              <h2 className="font-heading text-2xl font-medium text-charcoal">
                {t('listProperty.step1.title')}
              </h2>
              <p className="text-muted-foreground">{t('listProperty.step1.subtitle')}</p>

              <form onSubmit={handleAgentSubmit} className="space-y-4">
                <Input type="email" placeholder={t('listProperty.step1.email')} className="rounded-xl" value={agentData.email} onChange={(e) => setAgentData(prev => ({ ...prev, email: e.target.value }))} required />
                <Input placeholder={t('listProperty.step1.name')} className="rounded-xl" value={agentData.name} onChange={(e) => setAgentData(prev => ({ ...prev, name: e.target.value }))} required />
                <Input type="tel" placeholder={t('listProperty.step1.phone')} className="rounded-xl" value={agentData.phone} onChange={(e) => setAgentData(prev => ({ ...prev, phone: e.target.value }))} required />
                <Button type="submit" className="w-full rounded-full" disabled={loading}>
                  {loading ? "Chargement..." : t('listProperty.step1.continue')}
                </Button>
              </form>
            </div>
          )}

          {/* Step 2: Plan */}
          {step === 2 && (
            <div className="space-y-6">
              <h2 className="font-heading text-2xl font-medium text-charcoal">{t('listProperty.step2.title')}</h2>
              <p className="text-muted-foreground">{t('listProperty.step2.subtitle')}</p>

              <div className="grid md:grid-cols-3 gap-4">
                {plans.map(plan => (
                  <div 
                    key={plan.id}
                    onClick={() => setSelectedPlan(plan.id)}
                    className={`border-2 rounded-2xl p-6 cursor-pointer transition-all ${selectedPlan === plan.id ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'}`}
                  >
                    {plan.popular && <Badge className="bg-accent text-white mb-2">{t('listProperty.step2.popular')}</Badge>}
                    <h3 className="font-heading text-xl font-medium text-charcoal mb-2">{plan.name}</h3>
                    <p className="text-3xl font-bold text-primary mb-2">${plan.price}</p>
                    <p className="text-sm text-muted-foreground mb-4">{t('listProperty.step2.perMonth')}</p>
                    <ul className="space-y-2 text-sm text-muted-foreground">
                      {plan.features.map((f, i) => <li key={`${plan.id}-feat-${i}`}>✓ {f}</li>)}
                    </ul>
                  </div>
                ))}
              </div>

              <div className="flex gap-4">
                <Button variant="outline" onClick={() => setStep(1)} className="flex-1 rounded-full">Précédent</Button>
                <Button onClick={() => setStep(3)} className="flex-1 rounded-full">{t('listProperty.step2.continue')}</Button>
              </div>
            </div>
          )}

          {/* Step 3: Payment */}
          {step === 3 && (
            <div className="space-y-6">
              <h2 className="font-heading text-2xl font-medium text-charcoal">Paiement</h2>
              
              <div className="bg-secondary/50 rounded-xl p-6">
                <div className="flex justify-between items-center mb-4">
                  <span className="text-charcoal font-medium">{currentPlan?.name}</span>
                  <span className="text-2xl font-bold text-primary">${currentPlan?.price}/mois</span>
                </div>
                <p className="text-sm text-muted-foreground">Agent: {agentData.name}</p>
              </div>

              <div className="space-y-4">
                <Button onClick={handleStripePayment} className="w-full rounded-full h-14 text-lg" disabled={loading}>
                  <CreditCard className="w-5 h-5 mr-2" />
                  {loading ? "Traitement..." : "Payer par Carte"}
                </Button>

                <div className="relative">
                  <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-border"></div></div>
                  <div className="relative flex justify-center text-sm"><span className="px-2 bg-white text-muted-foreground">ou</span></div>
                </div>

                <a 
                  href="https://www.paypal.com/ncp/payment/SQPSBAGRQKCJW" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-full"
                >
                  <Button variant="outline" className="w-full rounded-full h-14 text-lg bg-[#FFC439] hover:bg-[#f0b72e] text-[#003087] border-[#FFC439]">
                    Payer avec PayPal
                  </Button>
                </a>

                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mt-4">
                  <p className="text-sm text-blue-800">
                    <strong>📌 Instructions PayPal :</strong><br/>
                    1. Cliquez sur "Payer avec PayPal"<br/>
                    2. Effectuez votre paiement<br/>
                    3. Revenez sur cette page<br/>
                    4. Envoyez votre confirmation de paiement à <strong>contact@dreamhousecanada.org</strong><br/>
                    5. Nous activerons votre compte sous 24h
                  </p>
                </div>
              </div>

              <Button variant="outline" onClick={() => setStep(2)} className="w-full rounded-full">Précédent</Button>
            </div>
          )}

          {/* Step 4: Add Properties */}
          {step === 4 && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="font-heading text-2xl font-medium text-charcoal">Ajouter une Propriété</h2>
                  <p className="text-muted-foreground">Forfait {currentPlan?.name}: {properties.length}/{currentPlan?.maxProperties} propriétés</p>
                </div>
                <Badge className="bg-green-500 text-white">Compte Actif</Badge>
              </div>

              {properties.length > 0 && (
                <div className="bg-secondary/50 rounded-xl p-4">
                  <h3 className="font-medium mb-2">Vos propriétés ({properties.length})</h3>
                  <ul className="space-y-2">
                    {properties.map((p, i) => (
                      <li key={`prop-${i}`} className="flex justify-between items-center text-sm">
                        <span>{p.title}</span>
                        <Link to="/properties" className="text-primary hover:underline">Voir</Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {canAddMore ? (
                <form onSubmit={handleAddProperty} className="space-y-4">
                  <Input placeholder="Titre de l'annonce *" className="rounded-xl" value={propertyForm.title} onChange={(e) => setPropertyForm(prev => ({ ...prev, title: e.target.value }))} required />
                  
                  <Textarea placeholder="Description détaillée" className="rounded-xl min-h-[100px]" value={propertyForm.description} onChange={(e) => setPropertyForm(prev => ({ ...prev, description: e.target.value }))} />
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <Input type="number" placeholder="Prix *" className="rounded-xl" value={propertyForm.price} onChange={(e) => setPropertyForm(prev => ({ ...prev, price: e.target.value }))} required />
                    <Select value={propertyForm.listing_type} onValueChange={(v) => setPropertyForm(prev => ({ ...prev, listing_type: v }))}>
                      <SelectTrigger className="rounded-xl"><SelectValue placeholder="Type d'annonce" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="buy">À Vendre</SelectItem>
                        <SelectItem value="rent">À Louer</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid md:grid-cols-4 gap-4">
                    <Select value={propertyForm.property_type} onValueChange={(v) => setPropertyForm(prev => ({ ...prev, property_type: v }))}>
                      <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="house">Maison</SelectItem>
                        <SelectItem value="condo">Condo</SelectItem>
                        <SelectItem value="apartment">Appartement</SelectItem>
                        <SelectItem value="chalet">Chalet</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input type="number" placeholder="Chambres" className="rounded-xl" value={propertyForm.bedrooms} onChange={(e) => setPropertyForm(prev => ({ ...prev, bedrooms: e.target.value }))} />
                    <Input type="number" placeholder="SdB" className="rounded-xl" value={propertyForm.bathrooms} onChange={(e) => setPropertyForm(prev => ({ ...prev, bathrooms: e.target.value }))} />
                    <Input type="number" placeholder="Superficie (pi²)" className="rounded-xl" value={propertyForm.sqft} onChange={(e) => setPropertyForm(prev => ({ ...prev, sqft: e.target.value }))} />
                  </div>

                  <div className="grid md:grid-cols-3 gap-4">
                    <Input placeholder="Adresse *" className="rounded-xl col-span-2" value={propertyForm.address} onChange={(e) => setPropertyForm(prev => ({ ...prev, address: e.target.value }))} required />
                    <Select value={propertyForm.province} onValueChange={(v) => setPropertyForm(prev => ({ ...prev, province: v }))}>
                      <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {provinces.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>

                  <Input placeholder="Ville *" className="rounded-xl" value={propertyForm.city} onChange={(e) => setPropertyForm(prev => ({ ...prev, city: e.target.value }))} required />
                  <Input placeholder="URL de l'image" className="rounded-xl" value={propertyForm.image_url} onChange={(e) => setPropertyForm(prev => ({ ...prev, image_url: e.target.value }))} />
                  <Input placeholder="Caractéristiques (séparées par virgule)" className="rounded-xl" value={propertyForm.features} onChange={(e) => setPropertyForm(prev => ({ ...prev, features: e.target.value }))} />

                  <Button type="submit" className="w-full rounded-full" disabled={loading}>
                    <Plus className="w-5 h-5 mr-2" />
                    {loading ? "Ajout en cours..." : "Ajouter la Propriété"}
                  </Button>
                </form>
              ) : (
                <div className="text-center py-8">
                  <p className="text-muted-foreground mb-4">Vous avez atteint la limite de votre forfait.</p>
                  <Button onClick={() => setStep(2)} className="rounded-full">Changer de forfait</Button>
                </div>
              )}

              <Link to="/properties">
                <Button variant="outline" className="w-full rounded-full">Voir toutes les propriétés</Button>
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ListPropertyPage;
